import { icon } from "@msuite/ui/icons";
import { useDisclosure } from "@msuite/ui/hooks";
import { Menu } from "@msuite/ui/menu";
import { useTranslation } from "@msuite/ui/localization";
import { type CellContext, RowActionsWrapper } from "@msuite/ui/table";
import type { ReturnReconciliationListItem } from "@msuite/utils/types";
import { Fragment } from "react";
import { useParams } from "react-router";
import { useCitadel } from "@/hooks/use-citadel";
import { BookReturnModal } from "@/modals/stock/book-return-modal";

/**
 * Booking material back onto a shelf, which is the one thing this panel exists to do.
 *
 * The run comes from the route rather than from the row: a reconciliation row is an article's
 * account for the *project*, so it names no return order, and the panel only ever renders under
 * `/organization/data/stock/return-orders/:returnOrderId`.
 *
 * Nothing here refetches. `BookReturnModal` invalidates the run's whole `return-order` segment on
 * success, which is what carries the new figures into this table and the carrier one beside it.
 */
export function RowActions({ cell }: CellContext<ReturnReconciliationListItem, "actions">) {
	/** Hooks */
	const { t } = useTranslation();
	const returnOrderId = useParams().returnOrderId as string;
	const bookModal = useDisclosure();

	/** Query */
	const { citadel } = useCitadel();

	/** Computed */
	// The same three-state guard `apps/web/src/components/citadel/can.tsx` documents: dropped only
	// on a definitive denial, because while the rules load the server keeps the last word. The
	// booking route gates on `update Stock` — the row it writes is a ledger entry, and the subject
	// of the request is the balance it moves.
	const stockUpdateIsDenied = !!citadel && citadel.cannot("update", "Stock");

	/** Render */
	return (
		<Fragment>
			<RowActionsWrapper>
				{!stockUpdateIsDenied && (
					<Menu
						items={() => [
							{
								label: t("stock.return_book"),
								onClick: bookModal.onOpen,
								icon: icon("PackagePlus"),
							},
						]}
					>
						<Menu.Button />
					</Menu>
				)}
			</RowActionsWrapper>
			{bookModal.isOpen && (
				<BookReturnModal
					isOpen={bookModal.isOpen}
					onClose={bookModal.onClose}
					returnOrderId={returnOrderId}
					row={cell.row.original}
				/>
			)}
		</Fragment>
	);
}
